/**
 * Which of the person's agents a prompt can go to right now, and what to call each.
 *
 * An agent is active while its hook (or the desktop runner) is asking for commands, or working on
 * one it was given. One that stopped asking more than a couple of minutes ago is not offered: a
 * prompt sent to it would sit until it expires.
 */

import type { RemoteAgent, RemoteMessage } from './types'

export const ACTIVE_WITHIN_MS = 2 * 60_000

export function activeAgents(agents: RemoteAgent[], now = Date.now()): RemoteAgent[] {
  return agents
    .filter((a) => (a.waiting || a.working) && now - new Date(a.lastPolledAt).getTime() <= ACTIVE_WITHIN_MS)
    .sort((a, b) => Number(b.waiting) - Number(a.waiting) || (a.repository ?? '').localeCompare(b.repository ?? ''))
}

/** "shop-api · desktop", "shop-api · a1b2c3": the repository, and which agent in it. */
export function agentLabel(agent: RemoteAgent): string {
  const where = agent.repository || '—'
  const which = agent.clientSessionId.startsWith('desktop-') ? 'desktop' : agent.clientSessionId.slice(0, 6)
  return `${where} · ${which}`
}

/** The target to use: the chosen one if it is still active, the only one if there is one, else any. */
export function resolveTarget(active: RemoteAgent[], chosen: string | null): string | null {
  if (chosen && active.some((a) => a.clientSessionId === chosen)) return chosen
  return active.length === 1 ? active[0]!.clientSessionId : null
}

/** The latest reply from an agent — or from any, when none is chosen. */
export function latestReply(messages: RemoteMessage[], clientSessionId: string | null): RemoteMessage | null {
  const replies = messages.filter((m) => m.direction === 'FromAgent' && (!clientSessionId || m.clientSessionId === clientSessionId))
  return replies.reduce<RemoteMessage | null>((last, m) => (!last || m.createdAt > last.createdAt ? m : last), null)
}
